import React, { useState } from 'react';
import TickerSearch from './TickerSearch';
import { LoadingState, ErrorState, NoDataState } from './LoadingStates';
import { useOpenInterestData } from '../hooks/useStockData';
import { OpenInterestChange, OiSortKey, OiFilterType } from '../types/StockData';

const OpenInterestChangesPage: React.FC = () => {
  const [currentTicker, setCurrentTicker] = useState<string>('GOOG');
  const [tickerInput, setTickerInput] = useState<string>('GOOG');
  const [sortBy, setSortBy] = useState<OiSortKey>('oi_change');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterType, setFilterType] = useState<OiFilterType>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { oiData, oiLoading, oiError, loadOpenInterestData } = useOpenInterestData(currentTicker);

  const handleTickerSearch = () => {
    setCurrentTicker(tickerInput);
  };

  const handleRefresh = () => {
    loadOpenInterestData(currentTicker);
  };

  // Parse option symbol to extract type, strike, and expiration
  const parseOptionSymbol = (symbol: string) => {
    const match = symbol.match(/([A-Z]+)(\d{6})([CP])(\d+)/);
    if (match) {
      const [, ticker, dateStr, type, strike] = match;
      
      // Parse date from YYMMDD format
      const year = parseInt('20' + dateStr.substring(0, 2));
      const month = parseInt(dateStr.substring(2, 4));
      const day = parseInt(dateStr.substring(4, 6));
      const expirationDate = new Date(year, month - 1, day);
      
      // Format expiration date
      const formattedDate = expirationDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: '2-digit'
      });
      
      return {
        ticker,
        date: dateStr,
        formattedDate,
        type: type === 'C' ? 'CALL' : 'PUT',
        strike: parseInt(strike) / 1000,
        expirationDate
      };
    }
    return null;
  };

  // Filter and sort data
  const filteredAndSortedData = React.useMemo(() => {
    let filtered = oiData;

    // Apply filters
    if (filterType === 'calls') {
      filtered = filtered.filter(item => parseOptionSymbol(item.option_symbol)?.type === 'CALL');
    } else if (filterType === 'puts') {
      filtered = filtered.filter(item => parseOptionSymbol(item.option_symbol)?.type === 'PUT');
    } else if (filterType === 'high_oi_change') {
      filtered = filtered.filter(item => item.oi_change > 1.0);
    } else if (filterType === 'high_volume') {
      filtered = filtered.filter(item => item.volume > 1000);
    }

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.option_symbol.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort data
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      // Use absolute values for oi_diff_plain sorting
      if (sortBy === 'oi_diff_plain') {
        aVal = Math.abs(aVal);
        bVal = Math.abs(bVal);
      }
      
      const multiplier = sortOrder === 'desc' ? -1 : 1;
      return (aVal > bVal ? 1 : -1) * multiplier;
    });

    return filtered;
  }, [oiData, filterType, searchTerm, sortBy, sortOrder]);

  const formatNumber = (num: number, decimals: number = 2) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toFixed(decimals);
  };

  const formatPercentage = (num: number) => {
    return `${(num * 100).toFixed(2)}%`;
  };

  if (oiLoading) {
    return <LoadingState />;
  }

  if (oiError) {
    return <ErrorState error={oiError} onRetry={handleRefresh} />;
  }

  if (!oiData || oiData.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="container mx-auto px-4 py-8">
          <header className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">
              Open Interest Changes
            </h1>
            <p className="text-gray-600">
              Track significant open interest changes for {currentTicker}
            </p>
          </header>

          <div className="max-w-md mx-auto mb-8">
            <TickerSearch 
              onTickerSearch={handleTickerSearch}
              currentTicker={currentTicker}
              onRefresh={handleRefresh}
              isLoading={oiLoading}
            />
          </div>

          <NoDataState 
            message={`No open interest changes data available for ${currentTicker}`}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-8">
        <header className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Open Interest Changes
          </h1>
          <p className="text-gray-600">
            Track significant open interest changes for {currentTicker}
          </p>
        </header>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Ticker Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ticker</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tickerInput}
                  onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleTickerSearch()}
                  placeholder="Enter ticker..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleTickerSearch}
                  disabled={oiLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  Search
                </button>
              </div>
            </div>

            {/* Filter Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as OiFilterType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Options</option>
                <option value="calls">Calls Only</option>
                <option value="puts">Puts Only</option>
                <option value="high_oi_change">High OI Change (&gt;100%)</option>
                <option value="high_volume">High Volume (&gt;1K)</option>
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as OiSortKey)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="oi_change">OI Change %</option>
                <option value="oi_diff_plain">OI Change (Absolute)</option>
                <option value="volume">Volume</option>
                <option value="curr_oi">Current OI</option>
                <option value="percentage_of_total">% of Total</option>
                <option value="days_of_oi_increases">Days of OI Increases</option>
                <option value="avg_price">Average Price</option>
                <option value="rnk">Rank</option>
              </select>
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Order</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Symbol</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Filter by symbol..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Total Options</h3>
            <p className="text-2xl font-bold text-gray-900">{filteredAndSortedData.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Avg OI Change</h3>
            <p className="text-2xl font-bold text-blue-600">
              {filteredAndSortedData.length > 0 
                ? `${(filteredAndSortedData.reduce((sum, item) => sum + item.oi_change, 0) / filteredAndSortedData.length).toFixed(1)}%`
                : '0%'
              }
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Total Volume</h3>
            <p className="text-2xl font-bold text-green-600">
              {formatNumber(filteredAndSortedData.reduce((sum, item) => sum + item.volume, 0), 0)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500">Total Premium</h3>
            <p className="text-2xl font-bold text-purple-600">
              ${formatNumber(filteredAndSortedData.reduce((sum, item) => sum + item.prev_total_premium, 0), 0)}
            </p>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Option Symbol
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type, Strike & Expiry
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    OI Change
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Volume
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current OI
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Premium
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Days Increasing
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedData.map((item, index) => {
                  const optionInfo = parseOptionSymbol(item.option_symbol);
                  const isCall = optionInfo?.type === 'CALL';
                  const oiChangeColor = item.oi_change > 0 ? 'text-green-600' : 'text-red-600';
                  
                  return (
                    <tr key={item.option_symbol} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-900">
                          {item.option_symbol}
                        </div>
                        <div className="text-xs text-gray-500">
                          {(item.percentage_of_total * 100).toFixed(2)}% of total
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-gray-900 mb-1">
                            ${optionInfo?.strike || 'N/A'} {optionInfo?.formattedDate || 'N/A'}
                          </div>
                          <div>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              isCall ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {optionInfo?.type || 'N/A'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className={`font-semibold ${oiChangeColor}`}>
                            {(item.oi_change * 100).toFixed(1)}%
                          </div>
                          <div className="text-gray-500">
                            {item.oi_diff_plain >= 0 ? '+' : ''}{formatNumber(item.oi_diff_plain, 0)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">
                            {formatNumber(item.volume, 0)}
                          </div>
                          <div className="text-xs space-x-1">
                            <span className="inline-flex px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                              {((item.prev_ask_volume / item.volume) * 100).toFixed(0)}% Ask
                            </span>
                            <span className="inline-flex px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                              {((item.prev_bid_volume / item.volume) * 100).toFixed(0)}% Bid
                            </span>
                            <span className="inline-flex px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                              {((item.prev_mid_volume / item.volume) * 100).toFixed(0)}% Mid
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">
                            {formatNumber(item.curr_oi, 0)}
                          </div>
                          <div className="text-xs text-gray-500">
                            was {formatNumber(item.last_oi, 0)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${item.avg_price.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${formatNumber(item.prev_total_premium, 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          item.days_of_oi_increases >= 5 
                            ? 'bg-green-100 text-green-800' 
                            : item.days_of_oi_increases >= 3
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {item.days_of_oi_increases} days
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {filteredAndSortedData.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No options match the current filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpenInterestChangesPage;
